import React, { useState, useEffect } from 'react';
import { Button, Table, PageTitle, Pagination, Tabs, Dropdown } from '@ui-modules';
import { useQuery } from 'react-query';
import './styles.scss';
import moment from 'moment';
import { useRepository } from '@context/repository.context';
import { IRequestListParams } from '@common/interfaces/Api';
import { useNavigate } from 'react-router-dom';
import { IStatusProperty } from '@common/interfaces/Common';
import { Input } from 'antd';
import { IDropdownOption } from '@ui-modules/types';

const { Search } = Input;

const processedFilterDropdownOptions = [
  { label: 'Quotes request sent', value: 'quotes_request_sent' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'Cancelled', value: 'cancelled' },
];

const getBookingStateBtn = (record: string): JSX.Element => {
  const statusProperties: IStatusProperty = {
    not_processed: {
      className: 'btn not_processed',
      variant: 'submit',
      text: 'Not processed',
    },
    quotes_request_sent: {
      className: 'btn quotes_request_sent',
      variant: 'transparent',
      text: 'Quotes request sent',
    },
    processed: {
      className: 'btn processed',
      variant: 'transparent',
      text: 'Processed',
    },
    assigned: {
      className: 'btn assigned',
      variant: 'transparent',
      text: 'Assigned',
    },
    cancelled: {
      className: 'btn cancelled',
      variant: 'submit',
      text: 'Cancelled',
    },
    defaultProperty: {
      className: 'btn btn-default',
      variant: 'submit',
    },
  };

  const additionalProperties = statusProperties[record] || statusProperties.defaultProperty;

  /* eslint-disable react/jsx-props-no-spreading */
  return <Button text={record} {...additionalProperties} />;
};

const columns = [
  {
    dataIndex: 'created',
    key: 'created',
    title: 'Request Date',
    sortDirections: ['descend', 'ascend'],
    defaultSortOrder: 'descend',
    sorter: true,
    render: (record: string) => {
      return moment(record).format('DD/MM/YYYY');
    },
  },
  {
    dataIndex: 'reference_code',
    key: 'reference_code',
    title: 'ID',
    sorter: true,
  },
  {
    dataIndex: 'requestor_first_name',
    key: 'requestor_first_name',
    title: 'Name',
  },
  {
    dataIndex: 'requestor_last_name',
    key: 'requestor_last_name',
    title: 'Last Name',
  },
  {
    dataIndex: 'requestor_organisation',
    key: 'requestor_organisation',
    title: 'Organisation',
  },
  {
    dataIndex: 'pickup_date',
    key: 'pickup_date',
    title: 'Start loading date',
    sortDirections: ['descend', 'ascend'],
    defaultSortOrder: 'descend',
    sorter: true,
    render: (record: string) => {
      return moment(record).format('DD/MM/YYYY');
    },
  },
  {
    dataIndex: 'status',
    key: 'status',
    title: 'Status',
    width: 200,
    render: (record: string) => {
      return getBookingStateBtn(record);
    },
  },
];

const tabsList = ['Not processed', 'Processed'];

const RequestsListPage = () => {
  const tabs = tabsList;
  const [selectedTab, setSelectedTab] = useState<string>(tabs[0]);
  const [columnOrder, setColumnOrder] = useState<string>('-created');
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchFilterValue, setSearchFilterValue] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<IDropdownOption | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const facilityId = +localStorage.getItem('facility')!;
  const country = localStorage.getItem('country');

  const navigate = useNavigate();
  const { freightRepository } = useRepository();

  useEffect(() => {
    setSelectedPage(1);
  }, [selectedTab]);

  useEffect(() => {
    if (!searchFilterValue) {
      setSearchFilter('');
    }
  }, [searchFilterValue]);

  const buildParams = (): IRequestListParams => {
    return {
      show: selectedTab === 'Processed' ? 'processed' : 'not_processed',
      offset: (selectedPage - 1) * 10,
      limit: pageSize,
      ordering: columnOrder || null,
      filter_query: searchFilter || null,
      status: statusFilter?.value || null,
    };
  };

  const { data: list, isLoading } = useQuery(
    [
      'freight-requests-list',
      selectedTab,
      selectedPage,
      pageSize,
      columnOrder,
      searchFilter,
      statusFilter,
    ],
    () => freightRepository.getRequestsList(facilityId, buildParams()),
    {
      refetchOnWindowFocus: false,
    },
  );

  const clearFilters = () => {
    setSearchFilterValue('');
    setSearchFilter('');
    setStatusFilter(null);
  };

  return (
    <section className="hbh-container booking-list">
      <PageTitle className="section-title" title={`Freight requests, ${country}`} bottomLine />
      <Tabs
        className="tabs"
        tabs={tabs}
        selectedTab={tabs.indexOf(selectedTab)}
        onTabSelect={(index) => {
          setSelectedTab(index);
          clearFilters();
        }}
      />
      <section className="filters">
        <div className="input-wrapper">
          <p className="input-label">Search Freight</p>
          <Search
            className="search-input"
            placeholder="Insert"
            value={searchFilterValue}
            onSearch={() => setSearchFilter(searchFilterValue)}
            onChange={(e) => setSearchFilterValue(e.target.value)}
            id="search-input"
            allowClear
            enterButton
          />
        </div>
        <div className="status-filter-dropdown">
          {selectedTab === 'Processed' && (
            <Dropdown
              className="filter status-filter outline"
              label={<p className="hbh-select-label">Filter by statuses</p>}
              value={statusFilter}
              placeholder="All statuses"
              options={processedFilterDropdownOptions}
              onChange={setStatusFilter}
            />
          )}
          <button type="button" onClick={clearFilters} className="reset">
            Reset filters
          </button>
        </div>
      </section>
      <div className="table">
        <Table
          columns={columns}
          data={list?.results}
          onRow={(record) => {
            return {
              onClick: () => navigate(`/requests/${record.uuid}`), // click row
            };
          }}
          rowKey="uuid"
          variant="dark"
          isLoading={isLoading}
          onChangeColumnOrder={setColumnOrder}
        />
      </div>
      <Pagination
        className="pagination"
        selectedPage={selectedPage}
        showJumper
        showPageSize
        totalPages={list?.count}
        variant="dark"
        onPageChange={setSelectedPage}
        onPageSizeChange={setPageSize}
      />
    </section>
  );
};

export default RequestsListPage;
